import { ActivatedRoute, NavigationEnd, Router } from '@angular/router'
import { select, Store } from '@ngrx/store'
import { Component, OnInit, OnDestroy } from '@angular/core'
import FormTrait from 'src/app/traits/form.trait'
import { debounceTime, Subject, Subscription } from 'rxjs'
import {
  CreateCompoundRequest,
  SetDefaultData,
  UpdateField,
} from '../compounds.actions'
import { getCreateCompoundState } from '../compounds.selectors'
import { isImmutable, Map } from 'immutable'
import Validator from '@shared/validatorjs/src/validator'
import { getPropertiesState } from '@containers/properties/properties.selectors'
import { FetchPropertiesRequest } from '@containers/properties/properties.actions'

@Component({
  selector: 'app-create-compound',
  templateUrl: './create-compound.component.html',
  styleUrls: ['./create-compound.component.css'],
})
export class CreateCompoundComponent
  extends FormTrait
  implements OnInit, OnDestroy
{
  public isLoading: boolean
  private state$: Subscription
  public isOpen = false
  public propertiesOptions = []
  public propertyAutocomplete$: Subscription
  public onPropertyAutocomplete$ = new Subject<string>()
  public properties$: Subscription
  public properties = []
  public selectedProperties = []

  constructor(
    public store: Store<any>,
    private route: ActivatedRoute,
    public router: Router,
  ) {
    super()
  }

  ngOnInit() {
    this.store.dispatch<any>(new SetDefaultData('create'))

    this.properties$ = this.store
      .pipe(select(getPropertiesState))
      .subscribe((state) => {
        this.properties = state.get('data').toJS()
        this.propertiesOptions = this.properties.map(
          ({ propertyCodeAssigned, id }) => ({
            name: propertyCodeAssigned,
            id: id,
          }),
        )
      })

    this.propertyAutocomplete$ = this.onPropertyAutocomplete$
      .pipe(debounceTime(500))
      .subscribe((keyword) => {
        if (keyword !== null) {
          this.store.dispatch<any>(
            new FetchPropertiesRequest(null, {
              filters: {
                or: [{ field: 'propertyCodeAssigned', value: keyword }],
                nullOr: ['compound'],
              },
              pageSize: 50,
            }),
          )
        }
      })

    this.state$ = this.store
      .pipe(select(getCreateCompoundState))
      .subscribe((state) => {
        this.state = state
        this.isLoading = state.get('isLoading')
      })

    this.store.dispatch<any>(
      new FetchPropertiesRequest(null, {
        filters: {
          nullOr: ['compound'],
        },
        pageSize: 50,
      }),
    )
  }

  ngOnDestroy() {
    this.state$.unsubscribe()
    this.propertyAutocomplete$.unsubscribe()
    this.properties$.unsubscribe()
  }

  public makePrimary(row: any) {
    this.store.dispatch(
      new UpdateField(
        'compoundProperties',
        this.getField(['compoundProperties'])
          .toJS()
          .map(({ propertyId, propertyCode }) => ({
            propertyCode,
            propertyId,
            isPrimary: row.get('propertyId') === propertyId,
          })),
        'create.data',
      ),
    )

    this.store.dispatch(
      new UpdateField(
        'validationErrors',
        this.state.get('validationErrors').delete('compoundProperties'),
        'create',
      ),
    )
  }

  public deleteProperty(row: any) {
    this.store.dispatch(
      new UpdateField(
        'compoundProperties',
        this.getField(['compoundProperties'])
          .toJS()
          .filter(({ propertyId }) => row.get('propertyId') !== propertyId),
        'create.data',
      ),
    )
  }

  public compareFn(item: any, selected: any) {
    return item.id == selected.id
  }

  public handleFieldChange(e: any, name: null | string = null): void {
    if (name === 'compoundProperties') {
      this.store.dispatch(
        new UpdateField(
          name,
          [
            ...this.getField(['compoundProperties']),
            ...e.map(({ id: propertyId, name: propertyCode }) => ({
              propertyId,
              propertyCode,
              isPrimary: false,
            })),
          ],
          'create.data',
        ),
      )
      this.isOpen = false
      this.selectedProperties = []
    } else {
      this.store.dispatch(
        new UpdateField(
          name || e.target.name,
          name ? e : e.target.value,
          'create.data',
        ),
      )
    }

    this.store.dispatch(
      new UpdateField(
        'validationErrors',
        this.state.get('validationErrors').delete(name || e.target.name),
        'create',
      ),
    )
  }

  validation() {
    const data = this.state.get('data')
    const rules = {
      name: 'required',
      compoundProperties: 'primary',
    }
    Validator.register(
      'primary',
      function (value, requirement, attribute) {
        return value.reduce((prev, curr) => prev || curr.isPrimary, false)
      },
      'The :attribute must contain a primary property.',
    )

    const validator = new Validator(data.toJS(), rules)

    validator.setAttributeNames({
      compoundProperties: 'properties',
    })

    return validator
  }

  public onSubmit(e: Event): void {
    e.preventDefault()
    const validation = this.validation()
    const payload = this.state.get('data').toJS()

    if (validation.passes()) {
      this.store.dispatch(new CreateCompoundRequest(payload))
    } else {
      this.store.dispatch<any>(
        new UpdateField('validationErrors', validation.errors.errors, 'create'),
      )
    }
  }
}
